import { useNavigate, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Screen, ScreenTitle, PrimaryButton, ErrorNote } from '../ui/Screen.jsx'
import { beeps as beepsApi, day0 as day0Api } from '../api.js'
import { nextClosedGate, isPermissionGranted } from '../onboardingSteps.js'
import { settingsRoute } from './Notifications.jsx'
import { detectPlatform } from '../pwa.js'
import { NavWordmark } from '../ui/NavBar.jsx'
import { buttonClasses } from '../ui/control.js'
import { useConfigBundle, screensOf } from '../config.js'
import { waitingLine, firstCheckInLine } from '../waiting.js'
import Ending from './Ending.jsx'

/**
 * Home and progress.
 *
 * THE PROGRESS INDICATOR IS BEEPS COMPLETED OUT OF 30 AND THE CURRENT DAY.
 * That is the whole of it. No score, no band, no interpretation, and no
 * encouragement copy that implies any of them — "you're doing great" is a
 * judgement about their answers and this app makes none.
 *
 * The denominator is always 30, never 29: a beep that was never notified still
 * expires to missed, so the count cannot drift (backend decision #27).
 */
/**
 * The one quiet link off the home screen.
 *
 * Signing out and leaving the study both live behind it. FINDABLE, NOT
 * PROMINENT: someone looking for the way out finds it in the obvious place,
 * and someone opening the app to answer a check-in is not offered a route to
 * quitting six times a day.
 */
/*
 * AD-5 defect 6 — this was a FOURTH button treatment: transparent, --ink-500,
 * 358px wide, radius 0, weight 400, sitting inline in the body. §4.3 permits
 * exactly three types and forbids `quiet` inline in the body entirely.
 *
 * It is the NavBar's right slot now. Home's left slot is the wordmark because
 * home is the root and there is nowhere out; the right slot is free, and
 * settings is the one place home leads that is not a check-in.
 */
function SettingsLink() {
  const navigate = useNavigate()
  return (
    <button type="button" onClick={() => navigate('/settings')} className={buttonClasses('quiet')}>
      Settings
    </button>
  )
}

/**
 * Shown on home when reminders are off AFTER day 0 — the half of the A2 split
 * that does not block.
 *
 * She is not stopped and nothing is demanded: she can answer every check-in
 * without this. But the reason her phone is silent is invisible otherwise, and
 * a participant who thinks the study forgot her behaves exactly like one who
 * decided to stop. So the explanation and the real per-platform route are
 * persistent rather than one-time.
 *
 * NOT --critical, and not an alert. Her notifications being off is a fact about
 * a setting, not an error she has made, and this screen is seen up to thirty
 * times.
 */
function RemindersOffNotice() {
  if (isPermissionGranted()) return null
  return (
    <div className="mb-[var(--s-5)] rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-sunken)] p-[var(--s-4)]">
      <p className="t-body text-[var(--ink-900)]">
        Reminders are off, so your phone won&rsquo;t tell you when a check-in opens. You can still
        open Editrics any time to see if one is waiting.
      </p>
      <ol className="mt-[var(--s-3)] flex flex-col gap-[var(--s-2)]">
        {settingsRoute(detectPlatform()).map((line, i) => (
          <li key={i} className="t-meta text-[var(--ink-700)]">
            {i + 1}. {line}
          </li>
        ))}
      </ol>
    </div>
  )
}

export default function Home() {
  const navigate = useNavigate()
  const { data: bundle } = useConfigBundle()

  /*
   * ONE `now`, CAPTURED ONCE PER RENDER — AD-11 §2.4 rule 4.
   *
   * The waiting estimate is computed from this and never from a timer. There is
   * deliberately no setInterval on this screen: if she leaves the app open the
   * number does not move, because a number that counts down is a countdown and
   * a countdown is an appointment. The query has no refetchInterval either, so
   * the only thing that recomputes it is a genuine refetch.
   */
  const now = Date.now()

  // WHERE THE PARTICIPANT SHOULD BE, decided by the server, not by whichever
  // screen happened to navigate here.
  //
  // This screen used to assume day 0 was behind the participant. Coming out of
  // onboarding — consent, install, notifications, "Not now" — landed them here,
  // and it said "Nothing to do right now" to someone who had never completed
  // demographics and never seen a day-0 item. They close the app; no beep ever
  // arrives, because the schedule is generated from wake and bed times that
  // were never collected. Stranded permanently, and reassured while stranded.
  //
  // Routing on server state rather than bolting a redirect onto the end of
  // onboarding means no path in — not a refresh, not a bookmark, not a
  // returning participant, not a notification tap — can reach "nothing to do"
  // while day 0 is unfinished.
  const day0State = useQuery({
    queryKey: ['day0', 'state'],
    queryFn: day0Api.state,
    staleTime: 0,
  })

  const current = useQuery({
    queryKey: ['beeps', 'current'],
    queryFn: beepsApi.current,
    staleTime: 15_000,
    // Only meaningful once day 0 is done; asking earlier just races the redirect.
    enabled: day0State.data?.complete === true,
  })

  if (day0State.isLoading) {
    return (
      <Screen support disposition="status" nav={{ left: <NavWordmark />, right: <SettingsLink /> }}>
        <ScreenTitle>Editrics</ScreenTitle>
        <p className="text-sm text-[var(--ink-700)]">One moment…</p>
      </Screen>
    )
  }

  if (day0State.isError) {
    return (
      <Screen support disposition="status" nav={{ left: <NavWordmark />, right: <SettingsLink /> }}>
        <ScreenTitle>Editrics</ScreenTitle>
        <ErrorNote error={day0State.error} />
      </Screen>
    )
  }

  const state = day0State.data
  if (state) {
    // In the order the study runs. Each is the server's own answer, never inferred.
    if (!state.consented_at) return <Navigate to="/consent" replace />
    if (!state.demographics_complete) return <Navigate to="/demographics" replace />

    /*
     * A2 — THE GATE BLOCKS BEFORE DAY 0 AND NEVER AFTER IT.
     *
     * The two halves look inconsistent and they are not. Please do not
     * "fix" one of them to match the other; they answer different questions.
     *
     * BEFORE day 0 is complete: hard block, installed AND granted, both
     * platforms. This is where enrolment engagement is set, she has invested
     * nothing yet, and the install step persuades nothing without it.
     *
     * AFTER day 0 is complete: NEVER block. She has given 221 items. If she
     * turns notifications off on day 3 — or the OS does it for her — a gate
     * here would lock her out of the study entirely and she could not submit
     * anything. That is data the study cannot recover, and it converts a
     * settings mistake into a lost participant. A participant with no
     * reminders who still opens the app is worth more than one locked out of
     * it.
     *
     * There is no escape hatch in either half: the gate is either closed or
     * absent. That is deliberate — an escape is a third state to reason about,
     * and the day-0 boundary already draws the line where it actually matters.
     */
    if (!state.complete) {
      const gate = nextClosedGate()
      if (gate) return <Navigate to={gate} replace />
    }

    if (!state.complete) return <Navigate to="/day0" replace />
  }

  if (current.isLoading || !current.data) {
    return (
      <Screen support disposition="status" nav={{ left: <NavWordmark />, right: <SettingsLink /> }}>
        <ScreenTitle>Editrics</ScreenTitle>
        <p className="text-sm text-[var(--ink-700)]">One moment…</p>
      </Screen>
    )
  }

  if (current.isError) {
    return (
      <Screen support disposition="status" nav={{ left: <NavWordmark />, right: <SettingsLink /> }}>
        <ScreenTitle>Editrics</ScreenTitle>
        <ErrorNote error={current.error} />
      </Screen>
    )
  }

  // Shape is backend's (#133), verified live. study_complete is FIRST because it
  // is tested first:
  // {study_complete, current, next_beep_at, day, days, completed, of}
  const data = current.data ?? {}
  const openBeep = data.current ?? null
  const completed = data.completed ?? 0
  // Always 30, never 29: a beep that was never notified still expires to missed.
  const total = data.of ?? 30
  // The day comes from stored rows (BE-12), so it is right between beeps too and
  // is never inferred from completed/6 — which would be wrong the moment anyone
  // misses a beep. Null before the schedule exists.
  const day = data.day ?? openBeep?.day ?? null
  const days = data.days ?? 5
  /*
   * NO LONGER AMBIGUOUS (#133). `next_beep_at: null` used to mean two opposite
   * things — "not scheduled yet" and "the study is over" — and this screen
   * assumed the first. It now names ONLY a beep that can still fire; which of
   * the two nulls this is comes from `study_complete`, which is read first.
   */
  const nextBeepAt = data.next_beep_at ?? null
  const waitingCopy = screensOf(bundle)?.waiting ?? null

  /*
   * BOTH LINES ARE COMPUTED ONCE, HERE, FROM ONE `now`. Neither refines, and
   * neither can become a clock time: the two `toLocaleTimeString` calls that
   * used to sit inline in the JSX below are gone, and the only way to put one
   * back is to write a new one.
   */
  const firstLine = firstCheckInLine(waitingCopy, nextBeepAt, now)
  const nextLine = waitingLine(waitingCopy, nextBeepAt, now)

  /*
   * ═══════════════════════════════════════════════════════════════════════
   * 1. STUDY COMPLETE. TESTED FIRST, ALWAYS. AD-11 §2.4, backend #133.
   *
   * The ORDER is the rule, and getting it backwards reintroduces the false
   * promise in a new place: on day 5 the bed-time beep is both the last of its
   * DAY and the last of the STUDY, so a screen that tested last-of-day first
   * would tell a finished participant "the next one comes tomorrow".
   *
   * `study_complete` is the SERVER's own fact (participant.completed_at) and is
   * never inferred here. The tempting inference is `completed === of` and it is
   * wrong for almost everyone — see Ending.jsx, where the reason lives with the
   * screen it protects.
   * ═══════════════════════════════════════════════════════════════════════
   */
  if (data.study_complete === true) {
    return <Ending nav={{ left: <NavWordmark />, right: <SettingsLink /> }} />
  }

  // FE-17: DO NOT SHOW A BARE ZERO TO SOMEONE WHO HAS JUST FINISHED DAY 0.
  //
  // Day 0 is 221 items and the longest, most disclosing thing anyone does here.
  // Finishing it used to land on a screen whose largest element was "0 of 30" —
  // the app's only counter reading nothing at the moment of the participant's
  // single largest effort. The number is not wrong; it is answering a question
  // nobody asked yet, because the check-ins have not started.
  //
  // So before the first beep fires, the screen says what is actually true: the
  // long part is done. The counter appears once it is counting something.
  const studyStarted = completed > 0 || day !== null || Boolean(openBeep)

  if (!studyStarted) {
    return (
      <Screen support disposition="status" nav={{ left: <NavWordmark />, right: <SettingsLink /> }}>
        <ScreenTitle>Editrics</ScreenTitle>
        <RemindersOffNotice />
        <div className="rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-raised)] p-5">
          <p className="t-section text-[var(--ink-900)]">That&rsquo;s the long part done</p>
          {/* CFG-41 A2: the em dash went because the sentence already carries a
              real hyphen in "check-ins", so two marks were doing two different
              jobs in one line. Restructured rather than hyphenated. */}
          <p className="mt-2 text-base leading-relaxed text-[var(--ink-700)]">
            Thank you. You&rsquo;ve finished the questions. From here it&rsquo;s six short
            check-ins a day for five days, a dozen questions at most.
          </p>
          {/*
            CFG-41 B2 / AD-11 §2.4 — THE CLOCK TIME IS DELETED, NOT ADJUSTED.
            This printed "Your first one arrives around 07:00", which was the
            single most plannable sentence in the app.

            The relative word is DERIVED from next_beep_at's local calendar
            date, which also fixes a claim that was outright false rather than
            merely over-disclosing: the old "tomorrow" branch is wrong for a
            day 0 finishing at 23:00, where the 12-hour floor lands after the
            next day's wake beep.

            null when the app does not know, and then nothing is rendered.
            Silence is honest; a promise is not.
          */}
          {firstLine ? (
            <p className="mt-3 text-base leading-relaxed text-[var(--ink-700)]">{firstLine}</p>
          ) : null}
        </div>
      </Screen>
    )
  }

  return (
    <Screen
      support
      disposition="status"
      nav={{ left: <NavWordmark />, right: <SettingsLink /> }}
      action={
        openBeep ? (
          /* the key is beep_id, not id */
          <PrimaryButton onClick={() => navigate(`/beep/${openBeep.beep_id}`)}>
            Start your check-in
          </PrimaryButton>
        ) : null
      }
    >
      <ScreenTitle>Editrics</ScreenTitle>

      <RemindersOffNotice />

      <div className="rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-raised)] p-5">
        <p className="text-base text-[var(--ink-700)]">Check-ins completed</p>
        <p className="mt-1 text-base font-semibold tabular-nums text-[var(--ink-900)]">
          {completed} <span className="t-body text-[var(--ink-500)]">of {total}</span>
        </p>
        {day ? (
          <p className="mt-3 text-base text-[var(--ink-700)]">
            Day {day} of {days}
          </p>
        ) : null}

        {/*
          §2.6 — THE BAR IS GONE, THE NUMBER STAYS. Build spec §9 requires the
          count, and a number is a record. A filled bar is an achievement meter,
          and on day four a bar sitting at 40% is the interface telling a
          struggling person they are failing a study that told them missing one
          is completely fine.
        */}
      </div>

      {/*
        ═════════════════════════════════════════════════════════════════════
        3-8. THE WAITING LINE. AD-11 §2.4, CFG-41 B3.

        The highest-exposure schedule claim in the app: home, every day,
        whenever nothing is open. It printed a CLOCK TIME —
        "Your next check-in is around 14:30" — and, when it had none,
        "We'll send you a reminder when the next one is ready."

        BOTH ARE DELETED, NEITHER IS REWORDED. The clock time is what let her
        arrange her day around the sampling, and the second is the sentence
        that lied at the end of the study: it was the null branch, and null
        meant both "not scheduled yet" and "it is over".

        What replaces them is a FUZZY estimate that cannot harden into an
        appointment, or nothing at all. `waitingLine` returns the lead alone
        when the app does not know — there is no branch here that could put a
        promise back, because there is no string here to put back.
        ═════════════════════════════════════════════════════════════════════
      */}
      {openBeep ? null : (
        <p className="mt-6 text-base leading-relaxed text-[var(--ink-700)]">{nextLine}</p>
      )}

    </Screen>
  )
}
